/**
 * Animal cohort filters — the shared filter contract for the animals list and,
 * later, any graph/report endpoint computed over the *same* cohort.
 *
 * This type is a **cohort definition, not list plumbing**: the number on a
 * future chart ("avg latest weight of the cows that calved in the last 30 days")
 * must be computed over exactly the set the filtered list shows. Both consumers
 * share this one type and one backend condition builder so the two WHEREs can
 * never drift. See docs/Plans/2026-07-13-animals-list-cohort-filtering.md.
 */
import { AnimalSex, LivestockMeasurementType, LivestockOperationType, LivestockStatus, } from "./Common.js";
/** Max ANDed record conditions per query — the DoS guard (each is a subquery). */
export const ANIMAL_RECORD_CONDITION_MAX = 5;
/** Max entries in a multi-select facet CSV (breeds, herdIds) per query. */
export const ANIMAL_FILTER_CSV_MAX = 25;
/**
 * Every cohort dimension, at **runtime**.
 *
 * `AnimalFilterDimensions` is a type, so it vanishes before any test can read
 * it — which is why the mapped type alone could not deliver the guarantee it
 * was introduced for ("a dimension exists but no transport advertises it is a
 * build failure"). The `satisfies Record<..., true>` below is the compile half:
 * adding a field to {@link AnimalFilters} without listing it here fails `tsc` in
 * *this* package, which a quality gate actually typechecks. The array is the
 * runtime half, so a transport's own suite can assert it advertises every
 * dimension it has not explicitly exempted.
 *
 * Keep the two halves adjacent; they are one mechanism.
 */
const ANIMAL_FILTER_DIMENSION_SET = {
    sex: true,
    breeds: true,
    herdIds: true,
    unassignedHerd: true,
    tagId: true,
    status: true,
    birthDateFrom: true,
    birthDateTo: true,
    acquisitionDateFrom: true,
    acquisitionDateTo: true,
    records: true,
    search: true,
};
/** The dimension names, for suites that must enumerate them at runtime. */
export const ANIMAL_FILTER_DIMENSIONS = Object.keys(ANIMAL_FILTER_DIMENSION_SET);
const OPERATION_TYPE_VALUES = new Set(Object.values(LivestockOperationType));
const MEASUREMENT_TYPE_VALUES = new Set(Object.values(LivestockMeasurementType));
const ANIMAL_SEX_VALUES = new Set(Object.values(AnimalSex));
const STATUS_VALUES = new Set(Object.values(LivestockStatus));
/**
 * The four top-level range dates. They reach `new Date(...)` unchecked in the
 * predicate builder, so an unvalidated `"last spring"` from a model would throw
 * at the driver instead of returning an error the caller can act on.
 */
const RANGE_DATE_KEYS = [
    "birthDateFrom",
    "birthDateTo",
    "acquisitionDateFrom",
    "acquisitionDateTo",
];
/** True when `value` is a valid ISO date string (or undefined). */
function isValidOptionalDate(value) {
    if (value === undefined)
        return true;
    return !Number.isNaN(new Date(value).getTime());
}
/**
 * Cross-check a single record condition against the right enum for its `kind`.
 * Returns an error message, or `null` when the condition is well-formed.
 */
function validateRecordCondition(condition, index) {
    if (condition.kind !== "operation" && condition.kind !== "measurement") {
        return `records[${index}].kind must be "operation" or "measurement"`;
    }
    if (condition.presence !== "has" && condition.presence !== "hasNot") {
        return `records[${index}].presence must be "has" or "hasNot"`;
    }
    const validTypes = condition.kind === "operation"
        ? OPERATION_TYPE_VALUES
        : MEASUREMENT_TYPE_VALUES;
    if (!validTypes.has(condition.type)) {
        return `records[${index}].type "${condition.type}" is not a valid ${condition.kind} type`;
    }
    if (!isValidOptionalDate(condition.from)) {
        return `records[${index}].from is not a valid date`;
    }
    if (!isValidOptionalDate(condition.to)) {
        return `records[${index}].to is not a valid date`;
    }
    return null;
}
/**
 * Validate a decoded {@link AnimalFilters} — enforces the record-condition cap,
 * the enum×kind cross-check, presence, date validity (record *and* the four
 * top-level range dates), the facet CSV caps, and the herd exclusion rule
 * (`unassignedHerd` cannot be combined with `herdIds`).
 *
 * Pure and framework-agnostic (no HTTP/error-class coupling): the caller maps a
 * `{ ok: false }` result to its own transport error (the list route → 400).
 * Shared by the list route now and any future stats/report endpoint that
 * accepts `AnimalFilters` directly in a POST body.
 */
export function validateAnimalFilters(filters) {
    if (filters.sex !== undefined && !ANIMAL_SEX_VALUES.has(filters.sex)) {
        return { ok: false, error: `sex "${filters.sex}" is not valid` };
    }
    if (filters.status !== undefined && !STATUS_VALUES.has(filters.status)) {
        return { ok: false, error: `status "${filters.status}" is not valid` };
    }
    if (filters.breeds && filters.breeds.length > ANIMAL_FILTER_CSV_MAX) {
        return {
            ok: false,
            error: `breeds accepts at most ${ANIMAL_FILTER_CSV_MAX} values`,
        };
    }
    if (filters.herdIds && filters.herdIds.length > ANIMAL_FILTER_CSV_MAX) {
        return {
            ok: false,
            error: `herdIds accepts at most ${ANIMAL_FILTER_CSV_MAX} values`,
        };
    }
    if (filters.unassignedHerd && filters.herdIds?.length) {
        return {
            ok: false,
            error: "unassignedHerd cannot be combined with herdIds",
        };
    }
    for (const key of RANGE_DATE_KEYS) {
        if (!isValidOptionalDate(filters[key])) {
            return { ok: false, error: `${key} is not a valid date` };
        }
    }
    if (filters.records) {
        if (filters.records.length > ANIMAL_RECORD_CONDITION_MAX) {
            return {
                ok: false,
                error: `at most ${ANIMAL_RECORD_CONDITION_MAX} record conditions are allowed`,
            };
        }
        for (let i = 0; i < filters.records.length; i++) {
            const error = validateRecordCondition(filters.records[i], i);
            if (error)
                return { ok: false, error };
        }
    }
    return { ok: true, filters };
}
//# sourceMappingURL=AnimalFilters.js.map